import { widgetScriptLocation } from 'constants/dom';
import { defaultFakeAgentName } from 'constants/general';
import { clientAgentId, techAgentId } from 'constants/history';
import { AnalyticsTarget, KeyAstromTarget } from 'enums/AnalyticsTarget';
import { BackgroundOperationType } from 'enums/BackgroundOperationStatus';
import { ChatViewMode } from 'enums/ChatViewMode';
import { CustomMessageType } from 'enums/CustomMessageType';
import { HistoryDumpFormat } from 'enums/HistoryDumpFormat';
import { HistoryRequestType } from 'enums/HistoryRequestType';
import { LocalScenario } from 'enums/LocalScenario';
import { MessageType } from 'enums/MessageType';
import { StorageKey } from 'enums/StorageKey';
import { WidgetFeature } from 'enums/WidgetFeature';
import { WidgetPlatform } from 'enums/WidgetPlatform';
import { WsAction } from 'enums/WsAction';
import { AgentData } from 'interfaces/AgentData';
import { BackgroundOperationData } from 'interfaces/BackgroundOperation';
import { BaseWsMessage } from 'interfaces/BaseWsMessage';
import { HoveringButtonInfo } from 'interfaces/HoveringButtonInfo';
import { InformationBanner } from 'interfaces/InformationBanner';
import { JsonObject } from 'interfaces/JsonObject';
import { MessageData, RenderMessageData } from 'interfaces/MessageData';
import { PhraseData } from 'interfaces/PhraseData';
import { WidgetParameters } from 'interfaces/WidgetParameters';
import { DefaultRootState } from 'react-redux';
import { Action } from 'redux';
import {
    all,
    call,
    cancel,
    cancelled,
    debounce,
    delay,
    fork,
    put,
    race,
    select,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';
import { services } from 'services/container';
import incomingMessageSoundUrl from 'sounds/incoming-message.mp3';
import outgoingMessageSoundUrl from 'sounds/outgoing-message.mp3';
import { receiveAgents } from 'state/agents/agents.actions';
import {
    BackgroundOperationActionType,
    cancelBackgroundOperation,
    CancelBackgroundOperationAction,
    finishBackgroundOperation,
    FinishBackgroundOperationAction,
    startBackgroundOperation,
    updateBackgroundOperation,
} from 'state/backgroundOperations/backgroundOperations.actions';
import { ConnectionActionType, SetConnectedAction } from 'state/connection/connection.actions';
import {
    selectChanneInformationBanner,
    selectIsExecutable,
} from 'state/connection/connection.selectors';
import { selectFeatureEnabled } from 'state/features/features.selectors';
import { openGameWidget } from 'state/game/game.actions';
import { closeInputWidget } from 'state/inputWidget/inputWidget.actions';
import { _counterLog } from 'utils/_counterLog';
import { wsToChatAgent } from 'utils/agents';
import { createReachGoalActionBy } from 'utils/analytics';
import { DtrumUtils } from 'utils/analytics/dtrumsutils';
import { ChatStateController } from 'utils/chatState';
import { setNetworkTimeDiff } from 'utils/dateTime';
import { blobToDataUrl, downloadFileFromUrl } from 'utils/files';
import {
    addPendingMessageToStorage,
    getPendingMessageFromStorage,
    isMessageReadable,
    loadPendingMessagesFromStorage,
    messageToRenderData,
    removePendingMessageFromStorage,
} from 'utils/messages';
import { AbortableFetcher } from 'utils/networking';
import { showBrowserNotification, vibrate } from 'utils/notifications';
import { randomUuid } from 'utils/random';
import { Sound } from 'utils/sound';
import { isVersionLater } from 'utils/versions';
import { widgetStore } from 'utils/widgetStore';

import placeholderUrl from '!url-loader!svg/40/img-placeholder.svg';

import {
    blinkMessage,
    cancelSearch,
    ChatActionType,
    closeChatOverlay,
    CurrentHistoryHtmlResponseAction,
    messagesHtmlRequest,
    resetPreAuthUnreadCounter,
    setAgentTyping,
    setHoveringButtons,
    setUnreadCounter,
    viewImageList,
} from '../chat/chat.actions';
import {
    addClientSideMessage,
    AddClientSideMessageAction,
    addCustomMessage,
    addInformationBannerMessage,
    AddMessageReactionAction,
    deleteClientSideMessage,
    DeleteClientSideMessageAction,
    DumpHistoryAction,
    focusMessage,
    goToBottom,
    GoToBottomAction,
    GoToUnreadReactionAction,
    HistoryActionType,
    loadPendingMessages,
    markMessageFavorite,
    MarkMessageFavoriteAction,
    pendingMessagesLoaded,
    ProcessScenarioAction,
    pruneFollowedMessages,
    reactionRead,
    ReactionReadAction,
    ReceiveHistoryAction,
    ReceiveMessageAction,
    RemoveMessageReactionAction,
    requestHistory,
    RequestHistoryAction,
    sendMessage,
    SendMessageAction,
    sendMessageRead,
    SendMessageReadAction,
    sendMessageReceived,
    SendMessageReceivedAction,
    sendMessageTimeout,
    setFirstUnreadMsgId,
    setInitialised,
    setLastMessageSentTimestamp,
    setMessageReactions,
    SetOperatorAvailableAction,
    setUnreadReactions,
    SetVisibleHistoryRangeAction,
    unmarkMessageFavorite,
} from './history.actions';
import {
    selectFollowedMessageCouple,
    selectLastMessage,
    selectMessageBy,
    selectUnreadCount,
} from './history.selectors';

const messageTimeout = 20; // Seconds
const incomingMsgSound = new Sound(incomingMessageSoundUrl, { volume: 0.4 });
const outgoingMsgSound = new Sound(outgoingMessageSoundUrl, { volume: 0.4 });

const queuedActions = [] as Action<HistoryActionType>[];
const queuedScenarios = [] as Action<HistoryActionType>[];

const isWebButton = (button: HoveringButtonInfo) =>
    !button.visibility_restrictions ||
    button.visibility_restrictions.length === 0 ||
    button.visibility_restrictions.find((restriction) => {
        const { platform, min_app_version, max_app_version } = restriction;
        if (platform && platform !== WidgetPlatform.Web) {
            return false;
        }

        if (min_app_version && !DEVELOPMENT && isVersionLater(min_app_version, BUILD_VERSION)) {
            return false;
        }

        if (max_app_version && !DEVELOPMENT && isVersionLater(BUILD_VERSION, max_app_version)) {
            return false;
        }

        return true;
    });

const selectClientSideMessage = (uuid: string) =>
    select((state: DefaultRootState) => state.history.clientSideMessages[uuid]);

function* flushQueuedHistoryActionsSaga() {
    yield takeLatest(HistoryActionType.FlushQueuedHistoryActions, function* () {
        try {
            for (const action of queuedActions) {
                yield put(action);
            }
        } catch (e) {
            services.log.error('Error during queued history actions flushing: ' + String(e));
        }

        queuedActions.length = 0;
    });
}

function* flushQueuedScenariosSaga() {
    yield takeLatest(HistoryActionType.FlushQueuedScenarios, function* () {
        try {
            for (const action of queuedScenarios) {
                yield put(action);
            }
        } catch (e) {
            services.log.error('Error during queued scenarios flushing: ' + String(e));
        }

        queuedScenarios.length = 0;
    });
}

function* requestHistorySaga() {
    yield debounce(500, HistoryActionType.RequestHistory, function* (action: RequestHistoryAction) {
        const { type, msgId, increasedChunk } = action.payload;

        const {
            history: { favoritesMode },
        }: DefaultRootState = yield select((state: DefaultRootState) => state);

        // If OlderAndNewer was passed, we perform two respective requests
        // and merge the received message lists. This allows emulation of
        // 'CENTER' history requests with messages both above and below
        // the requested ID. Useful for search and quotes navigation.
        const requestTypes =
            type === HistoryRequestType.OlderAndNewer
                ? [HistoryRequestType.Older, HistoryRequestType.Newer]
                : [type];

        let response: BaseWsMessage = null;

        for (const requestType of requestTypes) {
            const req: BaseWsMessage = {
                action: WsAction.HistoryRequest,
                data: {
                    is_favorite_only: favoritesMode,
                },
            };

            if (increasedChunk) {
                req.data.is_increased_chunk = true;
            }

            if (requestType && msgId) {
                req.data.type = requestType;
                req.data.message_id = msgId;
            }

            if (response) {
                const secondResponse = yield call(() => services.vo.wsRequest(req));
                response.data.messages = {
                    ...response.data.messages,
                    ...secondResponse.data.messages,
                };
            } else {
                response = yield call(() => services.vo.wsRequest(req));
            }
        }

        response.data.type = type;
        response.data.requestedMsgId = msgId;

        services.incomingAction.handle(response);
    });
}

function* requestReactionsSaga() {
    yield takeEvery(HistoryActionType.RequestReactions, function* () {
        const response = yield call(() =>
            services.vo.wsRequest({
                action: WsAction.UNREAD_REACTIONS_REQUEST,
            }),
        );
        yield put(setUnreadReactions(response.data));
    });
}

function* reactionReadSaga() {
    yield takeEvery(HistoryActionType.ReactionRead, function* (action: ReactionReadAction) {
        const msgId = action.payload;

        services.vo.wsTransmit(
            JSON.stringify({
                action: WsAction.REACTION_READ,
                data: {
                    message_id: msgId,
                },
            }),
        );
    });
}

function* goToUnreadReaction() {
    yield debounce(
        300,
        HistoryActionType.GoToUnreadReaction,
        function* (action: GoToUnreadReactionAction) {
            const state: DefaultRootState = yield select();
            const msgId = action.payload;
            const { messages } = state.history;

            if (msgId) {
                if (!messages?.[msgId]) {
                    yield put(requestHistory(HistoryRequestType.OlderAndNewer, msgId));
                }

                yield put(focusMessage(msgId));
                yield put(blinkMessage(msgId));
                yield put(reactionRead(msgId));
            }
        },
    );
}

function* receiveHistorySaga() {
    while (true) {
        let state: DefaultRootState = yield select();
        const { history: prevHistory, parameters: prevParameters } = state;

        const action: ReceiveHistoryAction = yield take(HistoryActionType.ReceiveHistory);
        const { history } = action.payload;
        const { type, operatorAvailable, chatNow, requestedMsgId, messages } = history;

        if (requestedMsgId) {
            if (type === HistoryRequestType.OlderAndNewer) {
                yield put(focusMessage(requestedMsgId, false));
                yield put(blinkMessage(requestedMsgId));
            }
        } else {
            const receivedMsgIds = Object.keys(messages);

            const msgIdToFocus =
                requestedMsgId ||
                (type === HistoryRequestType.Newer
                    ? receivedMsgIds[0]
                    : receivedMsgIds[receivedMsgIds.length - 1]);

            if (msgIdToFocus) {
                yield put(focusMessage(msgIdToFocus, false));
            }
        }

        state = yield select();

        const { fakeAgent, channel, unc } = state.parameters;
        const { lastReadByClientMessageId } = state.history;
        const { hoveringButtons } = state.chat;

        const isRenewHistory = prevParameters.channel !== channel || prevParameters.unc !== unc;
        const isInitialHistory = !prevHistory.initialised || prevHistory.bubbles.length === 0;

        const availableMsg = fakeAgent?.availableMsg;
        const unavailableMsg = fakeAgent?.unavailableMsg;

        // TODO сreate a individual saga to (re)set hoveringButtons
        if (isInitialHistory || isRenewHistory) {
            if (hoveringButtons) {
                yield put(setHoveringButtons(null));
            }

            if (!chatNow) {
                let hoveringButtons = state.connection.channelInfo?.hovering_buttons?.buttons;
                hoveringButtons = hoveringButtons?.filter(isWebButton);

                if (hoveringButtons && hoveringButtons.length > 0) {
                    yield put(setHoveringButtons(hoveringButtons));
                }
            }
        }

        if (isInitialHistory) {
            services.analytics.send(createReachGoalActionBy(AnalyticsTarget.SHOW_INITIAL_HISTORY));
            DtrumUtils.endAction(KeyAstromTarget.BUTTONCLICK_HISTORY_LOAD_DElAY);

            // It can also be null, therefore we check for true or false specifically
            if (operatorAvailable === true) {
                if (availableMsg) {
                    yield put(
                        addCustomMessage(CustomMessageType.Text, availableMsg as string, false),
                    );
                }
            } else if (operatorAvailable === false) {
                if (unavailableMsg) {
                    yield put(
                        addCustomMessage(CustomMessageType.Text, unavailableMsg as string, false),
                    );
                }
            }

            const lastMsgId = Object.values(messages).at(-1)?.id;
            _counterLog.log(`receiveHistorySaga`, `lastMsgId => ${lastMsgId}`);
            if (+lastMsgId > +lastReadByClientMessageId) {
                yield put(sendMessageRead(lastMsgId));
            } else if (lastMsgId && isRenewHistory) {
                yield put(sendMessageRead(lastMsgId));
            }

            yield put(loadPendingMessages());
            yield put(setInitialised(true));
        }

        if (isInitialHistory || isRenewHistory) {
            const informationBannerEnabled = yield select((state: DefaultRootState) =>
                selectFeatureEnabled(state, WidgetFeature.InformationBanner),
            );

            if (informationBannerEnabled) {
                const clientSideMessages: RenderMessageData[] = yield select(
                    (state: DefaultRootState) => state.history.clientSideMessages,
                );
                const hasInformationBanner =
                    Object.values(clientSideMessages)?.some(
                        (message) => message?.customType === CustomMessageType.InformationBanner,
                    ) ?? false;

                if (!hasInformationBanner) {
                    const informationBanner: InformationBanner = yield select(
                        selectChanneInformationBanner,
                    );
                    if (informationBanner) {
                        yield put(addInformationBannerMessage(informationBanner));

                        services.analytics.send(
                            createReachGoalActionBy(AnalyticsTarget.SHOW_INFORMATION_BANNER),
                        );
                    }
                }
            }
        }

        const soundNotificationsEnabled = yield select(
            (state: DefaultRootState) => state.chat.soundNotificationsEnabled,
        );

        if (soundNotificationsEnabled) {
            incomingMsgSound.preload();
            outgoingMsgSound.preload();
        }

        const unreadCounter = state.chat.preAuthUnreadCounter;
        let firstUnreadMsgId = null as string;

        if (unreadCounter > 0) {
            const msgIds = Object.keys(state.history.messages);
            if (msgIds.length >= unreadCounter) {
                firstUnreadMsgId = msgIds.at(-unreadCounter);

                yield put(resetPreAuthUnreadCounter());
            }
        }

        if (firstUnreadMsgId && !state.history.firstUnreadMsgId) {
            yield put(setFirstUnreadMsgId(firstUnreadMsgId));
        }
    }
}

function* sendMessageSaga() {
    yield takeEvery(HistoryActionType.SendMessage, function* (action: SendMessageAction) {
        const msgToSend = action.payload;

        const state: DefaultRootState = yield select();

        if (!state.chat.open) {
            ChatStateController.toggleChat(true);
        }

        if (!state.connection.authed) {
            queuedActions.push(action);
            return;
        }

        yield put(setLastMessageSentTimestamp(new Date()));

        const {
            chat: { hoveringButtons },
            history: { firstUnreadMsgId },
        } = state;

        if (firstUnreadMsgId) {
            yield put(setFirstUnreadMsgId(null));
        }

        if (hoveringButtons) {
            yield put(setHoveringButtons(null));
        }

        const { uuid, type, text, media_url, media_thumb, metadata, quote, files, signature } =
            msgToSend;
        const external_client_session_id = yield select(
            (state: DefaultRootState) => state.parameters.external_client_session_id,
        );

        if (isMessageReadable(msgToSend)) {
            const csMessageAction = addClientSideMessage({ ...msgToSend, receivedOnline: true });

            yield put(csMessageAction);

            if (state.inputWidget) {
                yield put(closeInputWidget());
            }

            addPendingMessageToStorage(csMessageAction.payload.message);
        }

        const data: JsonObject = {
            type,
            text,
            media_url,
            media_thumb,
            uuid,
            ...(metadata || external_client_session_id
                ? { metadata: { ...(metadata ?? {}), external_client_session_id } }
                : {}),
            files,
        };
        if (signature) {
            Object.assign(data, { signature });
        }

        if (files?.length) {
            services.analytics.send(createReachGoalActionBy(AnalyticsTarget.CLICK_FILES_SEND));
        }

        if (quote) {
            data.quote_message_id = quote.message_id;
            services.analytics.send(createReachGoalActionBy(AnalyticsTarget.CLICK_QUOTE_SEND));
        }

        services.vo.wsSend({
            action: WsAction.SendMessage,
            data,
        });

        const soundNotificationsEnabled = yield select(
            (state: DefaultRootState) => state.chat.soundNotificationsEnabled,
        );

        if (soundNotificationsEnabled) {
            outgoingMsgSound.play();
        }

        yield delay(messageTimeout * 1000);

        const pendingMsg = yield selectClientSideMessage(uuid);

        if (pendingMsg) {
            yield put(sendMessageTimeout(uuid));
            services.analytics.send(createReachGoalActionBy(AnalyticsTarget.SHOW_PENDING_MESSAGE));
        }
    });
}

function* addClientSideMessageSaga() {
    yield takeEvery(
        HistoryActionType.AddClientSideMessage,
        function* (action: AddClientSideMessageAction) {
            const { message, smoothScroll } = action.payload;

            yield put(focusMessage(message.id, smoothScroll));
        },
    );
}

function* deleteClientSideMessageSaga() {
    yield takeEvery(
        HistoryActionType.DeleteClientSideMessage,
        function* (action: DeleteClientSideMessageAction) {
            removePendingMessageFromStorage(action.payload);
        },
    );
}

function* loadPendingMessagesSaga() {
    yield takeEvery(HistoryActionType.LoadPendingMessages, function* () {
        const pendingMessages = loadPendingMessagesFromStorage();
        const pendingMessagesRenderData = Object.fromEntries(
            Object.entries(pendingMessages).map(([uuid, msg]) => [
                uuid,
                messageToRenderData({
                    ...msg,
                    time: Date.now() / 1000,
                } as MessageData),
            ]),
        );

        const state: DefaultRootState = yield select();
        const historyMessages = Object.values(state.history.messages);

        for (const [uuid] of Object.entries(pendingMessagesRenderData)) {
            if (historyMessages.find((m) => m.uuid === uuid)) {
                delete pendingMessagesRenderData[uuid];
            }
        }

        yield put(pendingMessagesLoaded(pendingMessagesRenderData));

        const pendingUuids = Object.keys(pendingMessagesRenderData);

        if (pendingUuids.length > 0) {
            yield put(focusMessage(pendingUuids.at(-1)));
        }
    });
}

const clearCustomMessageTypesOnReceive: { [type: string]: boolean } = {
    [CustomMessageType.CSI]: true,
    [CustomMessageType.CSICommentRequest]: true,
    [CustomMessageType.CSIThanks]: true,
};

const clearWidgetTypesOnReceive: { [type: string]: boolean } = {};

function* receiveMessageSaga() {
    yield takeEvery(HistoryActionType.ReceiveMessage, function* (action: ReceiveMessageAction) {
        const receivedMsg = action.payload;

        const isReadable = isMessageReadable(receivedMsg);

        setNetworkTimeDiff(Date.now() - receivedMsg.time * 1000);

        const state: DefaultRootState = yield select();

        const {
            history: { clientSideMessages, isCloseToBottom, firstUnreadMsgId },
            chat: { open, overlay, viewMode },
        } = state;

        for (const uuid of Object.keys(clientSideMessages)) {
            const match = uuid.match(/\[(.+)\]/);
            const customMsgType = match && match[1];

            if (customMsgType && clearCustomMessageTypesOnReceive[customMsgType]) {
                yield put(deleteClientSideMessage(uuid));
            }
        }

        if (isReadable && overlay && clearWidgetTypesOnReceive[overlay.type]) {
            yield put(closeChatOverlay());
        }

        if (
            isReadable &&
            open &&
            (isCloseToBottom || receivedMsg.agentId === clientAgentId) &&
            document.visibilityState === 'visible'
        ) {
            if (viewMode === ChatViewMode.Normal) {
                yield put(focusMessage(receivedMsg.id, true));
            }
        } else {
            if (!firstUnreadMsgId) {
                yield put(setFirstUnreadMsgId(receivedMsg.id));
            }
        }

        if (isReadable) {
            const notifEnabled = selectFeatureEnabled(state, WidgetFeature.BrowserNotifications);

            if (notifEnabled && (!open || document.visibilityState !== 'visible')) {
                let notifTitle: string;
                let notifText = receivedMsg.text;

                const agent = state.agents[receivedMsg.agentId];

                if (agent && agent.name) {
                    notifTitle = agent.name;
                } else {
                    notifTitle = defaultFakeAgentName;
                }

                if (receivedMsg.text) {
                    notifText = receivedMsg.text;
                } else if (receivedMsg.files || receivedMsg.media_url) {
                    notifText = 'Файл';
                } else {
                    notifText = 'Новое сообщение';
                }

                showBrowserNotification(notifTitle, notifText, receivedMsg.id);
            }

            if (state.inputWidget) {
                yield put(closeInputWidget());
            }
        }

        if (receivedMsg.agentId === clientAgentId) {
            const isPending = getPendingMessageFromStorage(receivedMsg.uuid);

            if (isPending) {
                removePendingMessageFromStorage(receivedMsg.uuid);

                if (
                    receivedMsg.type === MessageType.File ||
                    receivedMsg.type === MessageType.Image ||
                    receivedMsg.type === MessageType.Files
                ) {
                    services.analytics.sendAnalyticsAction(
                        'Отправка сообщения с файлом',
                        'Web-виджет. Отправка сообщения с файлом',
                    );
                }
            }
        } else {
            yield put(setAgentTyping(false));
            yield put(sendMessageReceived(receivedMsg.id));
        }
    });
}

// Saga полученных входящих сообщений
function* sendMessageReceivedSaga() {
    yield takeEvery(
        HistoryActionType.SendMessageReceived,
        function* (action: SendMessageReceivedAction) {
            const messageId = action.payload;

            services.vo.wsSend({
                action: WsAction.MessageReceived,
                data: {
                    id: [messageId],
                },
            });

            const state: DefaultRootState = yield select();

            if (state.chat.soundNotificationsEnabled) {
                incomingMsgSound.play();
            }

            if (state.chat.vibrationEnabled) {
                vibrate([200, 50, 50]);
            }
        },
    );
}

function* sendMessageReadSaga() {
    while (true) {
        let state: DefaultRootState = yield select();
        const {
            history: { lastReadByClientMessageId: _lastReadByClientMessageId },
        } = state;

        const action: SendMessageReadAction = yield take(HistoryActionType.SendMessageRead);
        const messageId = action.payload;

        services.vo.wsSend({
            action: WsAction.MessageRead,
            data: {
                id: messageId,
            },
        });

        state = yield select();
        const { viewMode } = state.chat;
        const { favoritesMode, lastReadByClientMessageId } = state.history;

        if (
            +lastReadByClientMessageId > +_lastReadByClientMessageId &&
            [ChatViewMode.Normal, ChatViewMode.Search].includes(viewMode) &&
            !favoritesMode
        ) {
            const count: number = yield select(selectUnreadCount);
            _counterLog.log(`sendMessageReadSaga`, `selectUnreadCount() => ${count}`);
            yield put(setUnreadCounter(count));
        }
    }
}

function* visibleRangeSaga() {
    yield takeEvery(
        HistoryActionType.SetVisibleRange,
        function* (action: SetVisibleHistoryRangeAction) {
            const state: DefaultRootState = yield select();

            const {
                history: {
                    loading,
                    messages,
                    bubbles,
                    currentBubbleIndex,
                    historyBeginningMsgId,
                    historyEndMsgId,
                    lastReadByClientMessageId,
                },
            } = state;

            const [firstVisibleId, lastVisibleId] = action.payload.visibleRange;
            const lastVisibleMsg = messages[lastVisibleId];

            _counterLog.log(
                `visibleRangeSaga`,
                `[firstVisibleId, lastVisibleId] => [${firstVisibleId}, ${lastVisibleId}]`,
            );
            if (
                lastVisibleMsg &&
                lastVisibleMsg.agentId !== clientAgentId &&
                +lastVisibleMsg.id > +lastReadByClientMessageId
            ) {
                yield put(sendMessageRead(lastVisibleMsg.id));
            }

            const bubble = bubbles[currentBubbleIndex] || [];

            const firstAvailableMsgId = bubble[0];
            const lastAvailableMsgId = bubble.at(-1);

            if (!loading) {
                if (
                    firstVisibleId === firstAvailableMsgId &&
                    firstAvailableMsgId !== historyBeginningMsgId
                ) {
                    yield put(requestHistory(HistoryRequestType.Older, firstVisibleId));
                }

                if (
                    lastVisibleId === lastAvailableMsgId &&
                    lastAvailableMsgId !== historyEndMsgId
                ) {
                    yield put(requestHistory(HistoryRequestType.Newer, lastVisibleId));
                }
            }
        },
    );
}

function* lookFollowedMessageSaga() {
    yield takeEvery(HistoryActionType.LookFollowedMessage, function* (action: GoToBottomAction) {
        const smooth = action.payload;
        const state: DefaultRootState = yield select((state) => state);
        const viewMode = state.chat.viewMode;

        if (viewMode === ChatViewMode.Normal) {
            const [followedMessageId, followedMessageIndex] = selectFollowedMessageCouple(state);
            const followedMessage = selectMessageBy(followedMessageId)(state);
            if (followedMessageId) {
                if (!followedMessage) {
                    yield put(requestHistory(HistoryRequestType.OlderAndNewer, followedMessageId));
                }

                yield put(focusMessage(followedMessageId, smooth));
                yield put(blinkMessage(followedMessageId));

                yield put(pruneFollowedMessages(followedMessageIndex + 1));
                return;
            }
        }

        yield put(goToBottom(smooth));
    });
}

function* goToBottomSaga() {
    yield takeEvery(HistoryActionType.GoToBottom, function* (action: GoToBottomAction) {
        const smooth = action.payload;
        const state: DefaultRootState = yield select((state) => state);
        const viewMode = state.chat.viewMode;

        if (viewMode === ChatViewMode.Normal) {
            const [followedMessageId, followedMessageIndex] = selectFollowedMessageCouple(state);
            const followedMessage = selectMessageBy(followedMessageId)(state);
            if (followedMessageId) {
                if (!followedMessage) {
                    yield put(requestHistory(HistoryRequestType.OlderAndNewer, followedMessageId));
                }

                yield put(focusMessage(followedMessageId, smooth));
                yield put(blinkMessage(followedMessageId));

                yield put(pruneFollowedMessages(followedMessageIndex + 1));
                return;
            }
        }

        const lastMsg = selectLastMessage(state);
        if (lastMsg) {
            const { historyEndMsgId } = state.history;

            if (lastMsg.clientSide || lastMsg.id === historyEndMsgId) {
                yield put(focusMessage(lastMsg.id, smooth));
            } else {
                yield put(requestHistory());
            }
        }
    });
}

function* operatorAvailableSaga() {
    yield takeEvery(
        HistoryActionType.SetOperatorAvailable,
        function* (action: SetOperatorAvailableAction) {
            const { operatorAvailable } = action.payload;

            const {
                fakeAgent: { availableMsg, unavailableMsg },
            }: WidgetParameters = yield select((state: DefaultRootState) => state.parameters);

            if (operatorAvailable === true) {
                if (availableMsg) {
                    yield put(addCustomMessage(CustomMessageType.Text, availableMsg as string));
                }
            } else if (operatorAvailable === false) {
                if (unavailableMsg) {
                    yield put(addCustomMessage(CustomMessageType.Text, unavailableMsg as string));
                }
            }
        },
    );
}

function* addMessageReactionSaga() {
    yield takeEvery(
        HistoryActionType.AddMessageReaction,
        function* (action: AddMessageReactionAction) {
            const { messageId, reactionId, clientId } = action.payload;
            const reactions = [{ id: reactionId, clientId }];
            yield put(setMessageReactions(messageId, reactions));
        },
    );
}

function* removeMessageReactionSaga() {
    yield takeEvery(
        HistoryActionType.RemoveMessageReaction,
        function* (action: RemoveMessageReactionAction) {
            const { messageId } = action.payload;
            const reactions = [];
            yield put(setMessageReactions(messageId, reactions));
        },
    );
}

function* markFavoriteSaga() {
    yield takeEvery(
        HistoryActionType.MarkMessageFavorite,
        function* (action: MarkMessageFavoriteAction) {
            const { msgId, predicted } = action.payload;

            if (!predicted) {
                let res: BaseWsMessage = null;

                try {
                    res = yield call([services.vo, 'wsRequest'], {
                        action: WsAction.MarkFavorite,
                        message_id: msgId,
                    });

                    const state: DefaultRootState = yield select();
                    const _viewImages = state.chat.viewImages;

                    if (_viewImages) {
                        yield put(
                            viewImageList({
                                ..._viewImages,
                                favorite: !_viewImages.favorite,
                            }),
                        );
                    }
                } finally {
                    if (!res?.is_success) {
                        yield put(unmarkMessageFavorite(msgId, true));
                    }
                }
            }
        },
    );
}

function* unmarkFavoriteSaga() {
    yield takeEvery(
        HistoryActionType.UnmarkMessageFavorite,
        function* (action: MarkMessageFavoriteAction) {
            const { msgId, predicted } = action.payload;

            if (!predicted) {
                let res: BaseWsMessage = null;

                try {
                    res = yield call([services.vo, 'wsRequest'], {
                        action: WsAction.UnmarkFavorite,
                        message_id: msgId,
                    });

                    const state: DefaultRootState = yield select();
                    const _viewImages = state.chat.viewImages;
                    if (_viewImages) {
                        yield put(
                            viewImageList({
                                ..._viewImages,
                                favorite: !_viewImages.favorite,
                            }),
                        );
                    }
                } finally {
                    if (!res?.is_success) {
                        yield put(markMessageFavorite(msgId, true));
                    }
                }
            }
        },
    );
}

function* processScenarioSaga() {
    yield takeLatest(
        HistoryActionType.ProcessDeeplinkScenario,
        function* (action: ProcessScenarioAction) {
            const state = yield select();

            const isExecutable = selectIsExecutable(state);

            if (!isExecutable) {
                queuedScenarios.push(action);
                return;
            }

            const scenarioId = action.payload;

            let res: PhraseData = null;
            try {
                res = yield call(() =>
                    services.vo.wsRequest({
                        action: WsAction.DeeplinkRequest,
                        id: scenarioId,
                    }),
                );
            } finally {
                const text = res?.text;

                if (text) {
                    yield put(
                        sendMessage({
                            type: MessageType.Text,
                            text,
                        }),
                    );

                    const ga = res.analytics?.google_analytics;
                    if (ga) {
                        services.analytics.sendAnalyticsAction(ga.action, ga.label);
                    }
                } else {
                    services.log.warn(`Failed to fetch scenario by ID (${scenarioId})`);
                }
            }
        },
    );
}

function* processLocalScenarioSaga() {
    yield takeEvery(
        HistoryActionType.ProcessDeeplinkLocalScenario,
        function* (action: ProcessScenarioAction) {
            const scenarioId = action.payload;

            switch (scenarioId) {
                case LocalScenario.PiggyGame: {
                    yield put(openGameWidget({ isInitiatedByDeepLink: false }));
                    break;
                }

                default:
                    break;
            }
        },
    );
}

const msgProgressPortion = 0.7;
const filesProgressPortion = 1 - msgProgressPortion;

function* dumpHtmlTask(operation: BackgroundOperationData) {
    const operationName = operation.title;
    const operationId = operation.uuid;

    const fetcher = new AbortableFetcher();

    try {
        const totalMessages: number = yield call(() => services.vo.totalMessageCountRequest());

        let firstAvailableMsgId: string = null;
        let messages: { [msgId: string]: MessageData } = {};
        let agents: { [agentId: string]: AgentData } = {};

        while (true) {
            const req: BaseWsMessage = {
                action: WsAction.HistoryRequest,
                data: {
                    type: HistoryRequestType.Older,
                    is_increased_chunk: true,
                },
            };

            if (firstAvailableMsgId) {
                req.data.message_id = firstAvailableMsgId;
            }

            const response = yield call(() => services.vo.wsRequest(req));
            const receivedMsgIds = Object.keys(response.data.messages);
            const firstReceivedMsgId = receivedMsgIds[0];

            if (firstReceivedMsgId !== firstAvailableMsgId) {
                firstAvailableMsgId = firstReceivedMsgId;

                messages = { ...response.data.messages, ...messages };
                agents = { ...response.data.agents, ...agents };

                yield put(
                    updateBackgroundOperation(
                        operationId,
                        operationName,
                        Math.min(
                            (Object.keys(messages).length / totalMessages) * msgProgressPortion,
                            msgProgressPortion,
                        ),
                    ),
                );
            } else {
                const normalizedAgents = Object.fromEntries(
                    Object.entries(agents)
                        .filter(([id]) => id !== techAgentId)
                        .map(([id, agent]) => [id, wsToChatAgent(agent)]),
                );

                yield put(receiveAgents(normalizedAgents));

                break;
            }
        }

        yield put(messagesHtmlRequest(Object.values(messages)));

        const { payload: markup }: CurrentHistoryHtmlResponseAction = yield take(
            ChatActionType.CurrentHistoryHtmlResponse,
        );

        const { resourceIds, html, totalFiles } = yield call(() => {
            const resourceIds: { [url: string]: string } = {};
            const html = markup.html.replaceAll(
                /"(https?:\/\/[^"]+\/fileStorage\/files\/[^"]+)"/gi,
                (_match, url: string) => {
                    if (url.indexOf('.mp3') !== -1) {
                        return url;
                    }

                    let uuid = resourceIds[url];
                    if (!uuid) {
                        uuid = randomUuid();
                        resourceIds[url] = uuid;
                    }
                    return `"${uuid}"`;
                },
            );
            const totalFiles = Object.keys(resourceIds).length;

            return Promise.resolve({ resourceIds, html, totalFiles });
        });

        const { resources } = yield call(function* () {
            let finishedFiles = 0;
            const resources = yield Promise.all(
                Object.entries<string>(resourceIds).map(async ([url, uuid]) => {
                    let dataStr: string = null;
                    try {
                        const response = await fetcher.fetch(url, {
                            credentials: 'include',
                            timeout: 60000,
                        });

                        if (response.ok) {
                            const blob = await response.blob();
                            dataStr = await blobToDataUrl(blob);
                        }
                    } catch (_) {
                    } finally {
                        finishedFiles++;
                    }

                    widgetStore.store.dispatch(
                        updateBackgroundOperation(
                            operationId,
                            'Скачивание файлов',
                            msgProgressPortion +
                                (finishedFiles / totalFiles) * filesProgressPortion,
                        ),
                    );

                    return [uuid, dataStr] as const;
                }),
            );
            return { resources };
        });

        const { url } = yield call(function* () {
            try {
                const historyDumpScript: string = yield fetcher
                    .fetch(`${widgetScriptLocation}/historyDump.js?v=${BUILD_VERSION}`)
                    .then((r) => r.text());

                const script = historyDumpScript.replace(
                    'HISTORY_DUMP_PARAMS',
                    JSON.stringify({
                        resources,
                        html,
                        placeholderUrl,
                    }),
                );

                const title = 'История переписки';
                const content =
                    `<!DOCTYPE html>` +
                    `<html lang="en">` +
                    `    <head>` +
                    `        <meta charset="UTF-8" />` +
                    `        <meta http-equiv="X-UA-Compatible" content="IE=edge" />` +
                    `        <meta name="viewport" content="width=device-width, initial-scale=1.0" />` +
                    `        <title>${title}</title>` +
                    `        <style>` +
                    `        html {` +
                    `           scroll-padding: 24px;` +
                    `        }` +
                    `        html, body {` +
                    `           margin: 0px;` +
                    `           padding: 0px;` +
                    `        }` +
                    `        ${markup.css}` +
                    `        </style>` +
                    `    </head>` +
                    `    <body>` +
                    `        <script defer>${script}</script>` +
                    `    </body>` +
                    `</html>`;

                const omitPublicUrlCheck = (content: string) => {
                    const matchText =
                        'if (!scriptUrl) throw new Error("Automatic publicPath is not supported in this browser");';
                    return content.replace(matchText, `//${matchText}`);
                };
                const file = new Blob([omitPublicUrlCheck(content)], { type: 'text/html' });
                const url = URL.createObjectURL(file);
                return { url };
            } catch (_) {}
        });

        downloadFileFromUrl(url, 'История.html');

        yield put(finishBackgroundOperation(operationId));
    } catch {
        fetcher.abort();
        yield put(cancelBackgroundOperation(operationId));
    } finally {
        if (yield cancelled()) {
            fetcher.abort();
        }
    }
}

function* dumpHistorySaga() {
    yield takeEvery(HistoryActionType.Dump, function* (action: DumpHistoryAction) {
        const { format } = action.payload;

        try {
            switch (format) {
                case HistoryDumpFormat.Txt: {
                    localStorage.setItem(
                        StorageKey.LastHistoryDumpTimestamp,
                        new Date().toISOString(),
                    );

                    const res: BaseWsMessage = yield call(() =>
                        services.vo
                            .wsRequest({
                                action: WsAction.HistoryDumpRequest,
                            })
                            .catch(() => {
                                localStorage.setItem(StorageKey.LastHistoryDumpTimestamp, '1');
                            }),
                    );

                    const { media_url } = res;
                    downloadFileFromUrl(services.vo.getThumbUrl(media_url));

                    break;
                }

                case HistoryDumpFormat.Html: {
                    const operation = startBackgroundOperation(
                        'Экспорт истории чата',
                        BackgroundOperationType.HistoryDump,
                    );

                    yield put(operation);

                    const dumpTask = yield fork(dumpHtmlTask, operation.payload);

                    while (true) {
                        const {
                            canceled,
                            finished,
                            connected,
                        }: {
                            canceled?: CancelBackgroundOperationAction;
                            finished?: FinishBackgroundOperationAction;
                            connected?: SetConnectedAction;
                        } = yield race({
                            canceled: take(BackgroundOperationActionType.Cancel),
                            finished: take(BackgroundOperationActionType.Finish),
                            connected: take(ConnectionActionType.SetConnected),
                        });

                        if (finished && finished.payload === operation.payload.uuid) {
                            localStorage.setItem(
                                StorageKey.LastHistoryDumpTimestamp,
                                new Date().toISOString(),
                            );

                            break;
                        }

                        if (canceled && canceled.payload === operation.payload.uuid) {
                            yield cancel(dumpTask);

                            break;
                        }

                        if (connected && connected.payload === false) {
                            yield put(cancelBackgroundOperation(operation.payload.uuid));
                            yield cancel(dumpTask);

                            break;
                        }
                    }

                    break;
                }
            }
        } catch (err) {
            services.log.error('Failed to dump history: ' + err);
        }
    });
}

function* clearHistorySaga() {
    yield takeEvery(HistoryActionType.Clear, function* () {
        const state: DefaultRootState = yield select();

        if (state.chat.searchQuery) {
            yield put(cancelSearch());
        }
    });
}

export function* historySaga() {
    yield all([
        requestHistorySaga(),
        requestReactionsSaga(),
        reactionReadSaga(),
        goToUnreadReaction(),
        receiveHistorySaga(),
        flushQueuedHistoryActionsSaga(),
        flushQueuedScenariosSaga(),
        sendMessageSaga(),
        addClientSideMessageSaga(),
        deleteClientSideMessageSaga(),
        loadPendingMessagesSaga(),
        receiveMessageSaga(),
        sendMessageReceivedSaga(),
        sendMessageReadSaga(),
        visibleRangeSaga(),
        lookFollowedMessageSaga(),
        goToBottomSaga(),
        operatorAvailableSaga(),
        markFavoriteSaga(),
        unmarkFavoriteSaga(),
        addMessageReactionSaga(),
        removeMessageReactionSaga(),
        processScenarioSaga(),
        processLocalScenarioSaga(),
        dumpHistorySaga(),
        clearHistorySaga(),
    ]);
}
